import { Link } from 'react-router-dom';
import { ArrowRight, Star, TrendingUp, TrendingDown, ChevronDown } from 'lucide-react';
import CourseQuiz from '@/components/CourseQuiz';

const TICKER_DATA = [
  { pair: 'EUR/USD', price: '1.0852', change: '+0.09%', up: true },
  { pair: 'GBP/USD', price: '1.2614', change: '-0.16%', up: false },
  { pair: 'USD/JPY', price: '158.80', change: '+0.10%', up: true },
  { pair: 'AUD/USD', price: '0.6513', change: '-0.12%', up: false },
  { pair: 'XAU/USD', price: '2348.74', change: '+0.14%', up: true },
  { pair: 'USD/CAD', price: '1.3586', change: '-0.43%', up: false },
  { pair: 'NZD/USD', price: '0.5950', change: '+0.28%', up: true },
  { pair: 'USD/CHF', price: '0.8771', change: '-0.47%', up: false },
];

function TickerMarquee() {
  const doubled = [...TICKER_DATA, ...TICKER_DATA];
  return (
    <div className="bg-[#0a0f1c] border-y border-white/10 py-2.5 overflow-hidden" data-testid="forex-ticker">
      <div className="flex animate-ticker whitespace-nowrap">
        {doubled.map((t, i) => (
          <div key={i} className="flex items-center gap-2.5 mx-6">
            <span className="text-white/70 font-semibold text-sm">{t.pair}</span>
            <span className="text-white font-mono text-sm">{t.price}</span>
            <span className={`flex items-center gap-0.5 font-mono text-xs ${t.up ? 'text-emerald-400' : 'text-red-400'}`}>
              {t.up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
              {t.change}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function HomePage() {
  return (
    <div className="min-h-screen overflow-x-hidden" data-testid="home-page">
      {/* Hero — full viewport, background image with overlay */}
      <section className="relative min-h-screen flex items-start xl:items-center overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/14749819/pexels-photo-14749819.jpeg)' }}
        >
          {/* Dark base overlay */}
          <div className="absolute inset-0 bg-[#050814]/55" />
          {/* Gradient color overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/8 via-transparent to-teal-500/8" />
          {/* Top vignette */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#050814]/60 via-transparent to-[#050814]/40" />
          {/* Subtle noise/grain texture */}
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=%270 0 256 256%27 xmlns=%27http://www.w3.org/2000/svg%27%3E%3Cfilter id=%27noise%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.65%27 numOctaves=%273%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23noise)%27/%3E%3C/svg%3E")' }} />
          {/* Bottom glow line */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/40 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-12 pt-28 sm:pt-32 pb-12 sm:pb-20 w-full">
          <div className="max-w-2xl">
            <h1 className="text-3xl xs:text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[1.05] tracking-tight break-words">
              Money powers the world
            </h1>
            <p className="mt-4 text-xl xs:text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-amber-500 font-light tracking-tight break-words">
              Knowledge powers traders
            </p>
            <p className="mt-5 sm:mt-6 text-sm sm:text-lg text-slate-300/80 leading-relaxed max-w-lg">
              Professional forex training and automated trading tools — 20+ years of market expertise, now at your fingertips.
            </p>
          </div>

          {/* Menu items — full width on desktop, stacked grid on mobile */}
          <div className="mt-10 sm:mt-14 mb-10 sm:mb-14 grid grid-cols-2 gap-x-3 gap-y-3 sm:flex sm:justify-between sm:items-start w-full min-w-0" data-testid="hero-menu">
            <div className="relative group min-w-0" data-testid="hero-nav-courses-wrapper">
              <Link
                to="/all-courses"
                className="flex items-center gap-1.5 text-xl xs:text-2xl sm:text-3xl lg:text-4xl font-black text-amber-500 hover:text-amber-400 transition-colors"
                data-testid="hero-nav-courses"
              >
                Courses
                <ChevronDown size={16} className="mt-1 text-amber-500/80 group-hover:text-amber-400 transition-colors shrink-0" />
              </Link>
              <div className="absolute top-full left-0 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-20">
                <div className="w-56 rounded-xl bg-[#0a0f1c]/95 backdrop-blur-md border border-white/10 py-2 shadow-2xl">
                  <Link to="/all-courses" className="block px-4 py-2.5 text-sm font-medium text-amber-400 hover:bg-white/5 transition-colors" data-testid="hero-subnav-all-courses">All Courses</Link>
                  <Link to="/courses" className="block px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-amber-400 hover:bg-white/5 transition-colors" data-testid="hero-subnav-beginners">Beginners</Link>
                  <Link to="/intermediate" className="block px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-amber-400 hover:bg-white/5 transition-colors" data-testid="hero-subnav-intermediate">Intermediate</Link>
                  <Link to="/advanced" className="block px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-amber-400 hover:bg-white/5 transition-colors" data-testid="hero-subnav-advanced">Advanced</Link>
                  <Link to="/products" className="block px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-amber-400 hover:bg-white/5 transition-colors" data-testid="hero-subnav-robots">Robots</Link>
                </div>
              </div>
            </div>
            <Link to="/products" className="text-xl xs:text-2xl sm:text-3xl lg:text-4xl font-black text-amber-500 hover:text-amber-400 transition-colors min-w-0" data-testid="hero-nav-robots">Robots</Link>
            <Link to="/affiliate" className="text-xl xs:text-2xl sm:text-3xl lg:text-4xl font-black text-amber-500 hover:text-amber-400 transition-colors min-w-0" data-testid="hero-nav-affiliates">Affiliates</Link>
            <Link to="/reviews" className="text-xl xs:text-2xl sm:text-3xl lg:text-4xl font-black text-amber-500 hover:text-amber-400 transition-colors min-w-0" data-testid="hero-nav-reviews">Reviews</Link>
            <Link to="/contact" className="text-xl xs:text-2xl sm:text-3xl lg:text-4xl font-black text-amber-500 hover:text-amber-400 transition-colors min-w-0" data-testid="hero-nav-contacts">Contacts</Link>
          </div>

          <div className="max-w-2xl">
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/forex-made-simple"
                className="inline-flex items-center gap-2 bg-amber-500 text-[#050814] hover:bg-amber-400 rounded-full px-8 py-4 text-base font-bold glow-amber transition-all"
                data-testid="hero-cta-start"
              >
                Start Learning <ArrowRight size={18} />
              </Link>
              <Link
                to="/products"
                className="inline-flex items-center gap-2 border border-white/20 text-white hover:bg-white/10 rounded-full px-8 py-4 text-base font-medium transition-colors"
                data-testid="hero-cta-products"
              >
                Browse Robots
              </Link>
            </div>
            <div className="mt-10 sm:mt-12 flex flex-wrap items-center gap-x-5 gap-y-3">
              <div className="flex items-center gap-2">
                <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-amber-500 text-amber-500" />)}</div>
                <span className="text-slate-400 text-xs sm:text-sm">4.9/5 Forex Peace Army</span>
              </div>
              <span className="text-slate-400 text-xs sm:text-sm"><span className="text-white font-bold">20+</span> Years</span>
              <span className="text-slate-400 text-xs sm:text-sm"><span className="text-white font-bold">29</span> Countries</span>
            </div>
          </div>
        </div>
      </section>

      <TickerMarquee />

      {/* Find your path — All Courses + Quiz + Automate */}
      <section className="py-16 sm:py-24 bg-[#050814]" data-testid="value-section">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 md:px-12">
          <CourseQuiz />

          <div className="mt-8 sm:mt-10 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="value-cards">
            <Link
              to="/all-courses"
              className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 sm:p-6 hover:border-amber-400/30 hover:bg-amber-400/[0.03] transition-all"
              data-testid="value-all-courses"
            >
              <p className="text-2xl sm:text-3xl font-black text-amber-500 mb-1">All Courses</p>
              <p className="text-slate-400 text-sm leading-relaxed">Three structured levels — Beginner, Intermediate, Advanced. Pick the path that fits where you are today.</p>
              <span className="inline-flex items-center gap-1 mt-4 text-sm font-bold text-amber-500 group-hover:text-amber-400 transition-colors">
                Explore Courses <ArrowRight size={14} />
              </span>
            </Link>
            <Link
              to="/products"
              className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 sm:p-6 hover:border-amber-400/30 hover:bg-amber-400/[0.03] transition-all"
              data-testid="value-automate"
            >
              <p className="text-2xl sm:text-3xl font-black text-amber-500 mb-1">Automate</p>
              <p className="text-slate-400 text-sm leading-relaxed">Professional MT4 &amp; MT5 Expert Advisors. Let battle-tested algorithms trade while you sleep.</p>
              <span className="inline-flex items-center gap-1 mt-4 text-sm font-bold text-amber-500 group-hover:text-amber-400 transition-colors">
                Browse Robots <ArrowRight size={14} />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonial — single featured */}
      <section className="py-16 sm:py-24 bg-[#0a0f1c]" data-testid="testimonials-section">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 md:px-12 text-center">
          <div className="flex justify-center gap-1 mb-5 sm:mb-6">
            {[...Array(5)].map((_, i) => <Star key={i} size={18} className="fill-amber-500 text-amber-500" />)}
          </div>
          <p className="text-lg sm:text-xl md:text-2xl text-white/80 leading-relaxed font-light italic">
            &ldquo;Hettie&apos;s EAs have transformed my trading. The Mother EA alone has generated consistent returns over 8 months. Best investment I&apos;ve made.&rdquo;
          </p>
          <p className="mt-5 sm:mt-6 text-amber-500 font-bold">Marcus Chen</p>
          <p className="text-slate-500 text-sm">Professional Trader</p>
          <Link to="/reviews" className="inline-flex items-center gap-2 mt-6 sm:mt-8 text-sm font-bold text-slate-400 hover:text-white transition-colors">
            Read all 71 reviews <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* CTA — clean, minimal */}
      <section className="py-16 sm:py-24 bg-[#050814]" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 md:px-12 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white tracking-tight">Start your trading journey today</h2>
          <p className="mt-3 sm:mt-4 text-slate-400 text-base sm:text-lg">No experience needed. Begin with our Forex Made Simple beginner course and work your way up.</p>
          <div className="mt-6 sm:mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center gap-2 border border-white/20 text-white hover:bg-white/10 rounded-full px-7 sm:px-8 py-3 sm:py-4 text-base font-medium transition-colors" data-testid="cta-contact">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
